import * as IconSpec from '@mc/react-ui-icons';
import {EditorToolType, ExtendedEditorToolDefinition} from '@mc/react-mapio';

export type IconName = keyof typeof IconSpec;

type ToolDescriptor = {
    type: EditorToolType;
    name: string;
    icon: IconName;
    iconActive?: IconName;
};

const baseTools: Partial<Record<EditorToolType, ToolDescriptor>> = {
    ADD_TO_COLLECTION: {type: EditorToolType.ADD_TO_COLLECTION, name: 'Nový', icon: 'mcNew'},
    REMOVE_FROM_COLLECTION: {type: EditorToolType.REMOVE_FROM_COLLECTION, name: 'Odebrat z kolekce', icon: 'mcDisconnect'},
    POINT: {type: EditorToolType.POINT, name: 'Bod', icon: 'mcCircle'},
    POINT_COORDS: {type: EditorToolType.POINT_COORDS, name: 'Bod souřadnicemi', icon: 'mcEditPointCoordinates'},
    LINE: {type: EditorToolType.LINE, name: 'Linie', icon: 'mcEditLine'},
    LINE_COORDS: {type: EditorToolType.LINE_COORDS, name: 'Linie souřadnicemi', icon: 'mcEditLineCoordinates'},
    LINE_OFFSET: {type: EditorToolType.LINE_OFFSET, name: 'Linie s offsetem', icon: 'mcParallelLinesOffset'},
    LINE_PARTIAL_OFFSET: {type: EditorToolType.LINE_PARTIAL_OFFSET, name: 'Část linie s offsetem', icon: 'mcMapMove'},
    LINE_EXTENSION: {type: EditorToolType.LINE_EXTENSION, name: 'Rozšíření čáry', icon: 'mcSize'},
    POLYGON: {type: EditorToolType.POLYGON, name: 'Polygon', icon: 'mcSelectPolygon'},
    POLYGON_COORDS: {type: EditorToolType.POLYGON_COORDS, name: 'Polygon souřadnicemi', icon: 'mcSelectPolygon'},
    FIXED_RECTANGLE: {type: EditorToolType.FIXED_RECTANGLE, name: 'Obdélník s pevnými rozměry', icon: 'mcSelectRectangle'},
    CIRCLE: {type: EditorToolType.CIRCLE, name: 'Kruh', icon: 'mcProgressRing0'},
    CIRCLE_RADIUS: {type: EditorToolType.CIRCLE_RADIUS, name: 'Kruh s poloměrem', icon: 'mcRingRadius'},
    CIRCLE_COORDS: {type: EditorToolType.CIRCLE_COORDS, name: 'Kruh souřadnicemi', icon: 'mcRingCoordinates'},
    GUIDED_LINE: {type: EditorToolType.GUIDED_LINE, name: 'Kolmice a rovnoběžky', icon: 'mcPerpendicularParallels'},
    GUIDED_POLYGON: {type: EditorToolType.GUIDED_POLYGON, name: 'Kolmice a rovnoběžky', icon: 'mcSelectPolygon'},
    PERPENDICULAR_LINE: {type: EditorToolType.PERPENDICULAR_LINE, name: 'Kolmice', icon: 'mcDistanceVertical'},
    MEASURE_LINE: {type: EditorToolType.MEASURE_LINE, name: 'Vzdálenost', icon: 'mcDistance'},
    MEASURE_POLYGON: {type: EditorToolType.MEASURE_POLYGON, name: 'Plocha', icon: 'mcSelectPolygon'},
    SELECT: {type: EditorToolType.SELECT, name: 'Bodem', icon: 'mcCircle'},
    EXACT_SELECT: {type: EditorToolType.EXACT_SELECT, name: 'Bodem', icon: 'mcCircle'},
    EXACT_SINGLE_SELECT: {type: EditorToolType.EXACT_SINGLE_SELECT, name: 'Bodem (single)', icon: 'mcCircle'},
    BOX_SELECT: {type: EditorToolType.BOX_SELECT, icon: 'mcSelectRectangle', name: 'Obdélníkem'},
    POLY_SELECT: {type: EditorToolType.POLY_SELECT, icon: 'mcSelectPolygon', name: 'Polygonem'},
    POLY_SELECT_INTERSECT: {
        type: EditorToolType.POLY_SELECT_INTERSECT,
        icon: 'mcSelectPolygon',
        name: 'Vybrat existujícím polygonem',
    },
    CIRCLE_SELECT: {type: EditorToolType.CIRCLE_SELECT, icon: 'mcProgressRing0', name: 'Kružnicí'},
    LINE_SELECT: {type: EditorToolType.LINE_SELECT, icon: 'mcEditLine', name: 'Linií'},
    BOX_ZOOM: {type: EditorToolType.BOX_ZOOM, icon: 'mcSelectPolygon', name: 'Obdélníkem'},
    TEMP_EDITING: {type: EditorToolType.TEMP_EDITING, name: 'Editovat vybraný objekt', icon: 'mcSettings'},
    EDIT: {type: EditorToolType.EDIT, name: 'Editace', icon: 'mcEdit'},
    EDIT_MOVE: {type: EditorToolType.EDIT_MOVE, name: 'Editace a posuv', icon: 'mcEdit'},
    MOVE: {type: EditorToolType.MOVE, name: 'Posuv', icon: 'mcMapMove'},
    ROTATE: {type: EditorToolType.ROTATE, name: 'Otočení', icon: 'mcRefresh'},
    ROTATE_MOVE: {type: EditorToolType.ROTATE_MOVE, name: 'Otočení a posuv', icon: 'mcRefresh'},
    OFFSET: {type: EditorToolType.OFFSET, name: 'Offset (odsazení)', icon: 'mcHide'},
    CUT: {type: EditorToolType.CUT, name: 'Vyjmout', icon: 'mcCut'},
    COPY: {type: EditorToolType.COPY, name: 'Kopírovat', icon: 'mcCopy'},
    COPY_TO_CLIPBOARD: {type: EditorToolType.COPY_TO_CLIPBOARD, name: 'Kopírovat do schránky', icon: 'mcCopy'},
    DUPLICATE: {type: EditorToolType.DUPLICATE, name: 'Duplikovat', icon: 'mcDuplicate'},
    PASTE: {type: EditorToolType.PASTE, name: 'Vložit', icon: 'mcDuplicate'},
    DELETE: {type: EditorToolType.DELETE, name: 'Smazat', icon: 'mcDelete'},
    MERGE: {type: EditorToolType.MERGE, name: 'Spojit', icon: 'mcDistance'},
    MERGE_LINES: {type: EditorToolType.MERGE_LINES, name: 'Spojit čáry', icon: 'mcDistance'},
    SPLIT: {type: EditorToolType.SPLIT, name: 'Rozpojit', icon: 'mcDividerArrows'},
    SPLIT_LINE: {type: EditorToolType.SPLIT_LINE, name: 'Rozdělení čáry', icon: 'mcStretch'},
    SPLIT_POLYGON: {type: EditorToolType.SPLIT_POLYGON, name: 'Rozdělení polygonu', icon: 'mcProgressRing50'},
    SNAP: {type: EditorToolType.SNAP, name: 'Přichytávání', icon: 'mcAttachementNonactive', iconActive: 'mcAttachementActive'},
    BUFFER: {type: EditorToolType.BUFFER, name: 'Buffer', icon: 'mcLink'},
    SHOW_COORDS: {type: EditorToolType.SHOW_COORDS, name: 'Sejmutí souřadnic', icon: 'mcEditPointCoordinates'},
    SHOW_CONNECTIONS: {type: EditorToolType.SHOW_CONNECTIONS, name: 'Zobrazit spojení', icon: 'mcDistance'},
    INSPECT: {type: EditorToolType.INSPECT, name: 'Informace o objektu', icon: 'mcHelp'},
    EXPORT_FEATURES: {type: EditorToolType.EXPORT_FEATURES, name: 'Exportovat objekty', icon: 'mcExportLayers'},
    EXPORT_LAYERS: {type: EditorToolType.EXPORT_LAYERS, name: 'Exportovat vrstvy', icon: 'mcExportLayers'},
    PRINT: {type: EditorToolType.PRINT, name: 'Tisk výřezu', icon: 'mcPrint'},
    ORIGIN_LOCATION: {type: EditorToolType.ORIGIN_LOCATION, name: 'Výchozí poloha', icon: 'mcHome'},
    GEOLOCATION: {type: EditorToolType.GEOLOCATION, name: 'Geolokace', icon: 'mcMapPosition'},
    SHOW_LOCATION: {type: EditorToolType.SHOW_LOCATION, name: 'Umístit špendlík', icon: 'mcPinFill'},
    CUSTOM_ACTION: {type: EditorToolType.CUSTOM_ACTION, name: 'Akce', icon: 'mcSettings'},
    CUTOUT_DRAW: {type: EditorToolType.CUTOUT_DRAW, name: 'Zákresem', icon: 'mcCurves'},
    CUTOUT_REMOVE: {type: EditorToolType.CUTOUT_REMOVE, name: 'Mazání výřezu', icon: 'mcDelete'},
    CUTOUT_FIXED_RECTANGLE: {type: EditorToolType.CUTOUT_FIXED_RECTANGLE, name: 'Obdélníkem', icon: 'mcRectangle'},
    CUTOUT_PASTE: {type: EditorToolType.CUTOUT_PASTE, name: 'Vložit výřez', icon: 'mcDuplicate'},
    CUTOUT_FROM_EXISTING: {type: EditorToolType.CUTOUT_FROM_EXISTING, name: 'Z existujícího', icon: 'mcLocationPlace'},
    MULTI_FIXED_RECTANGLE: {type: EditorToolType.MULTI_FIXED_RECTANGLE, name: 'Obdélník', icon: 'mcRectangle'},
    MULTI_ADD_EXISTING: {type: EditorToolType.MULTI_ADD_EXISTING, name: 'Existující', icon: 'mcGroup'},
    DELETE_FEATURES_GEOMETRY: {type: EditorToolType.DELETE_FEATURES_GEOMETRY, name: 'Smazat geometrii', icon: 'mcDelete'},
};

export const getToolInfo = (toolId: string | ExtendedEditorToolDefinition) => {
    if (typeof toolId === 'string') {
        const toolInfo = baseTools[toolId];
        if (toolInfo) {
            return toolInfo;
        } else {
            return toolId;
        }
    } else {
        return toolId;
    }
};
